/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useMemo, useEffect, useRef} from 'react'
import {useIntl, defineMessage} from 'react-intl'

// Chakra Components
import {
    Box,
    Stack,
    Grid,
    GridItem,
    useDisclosure,
    Button,
    useMultiStyleConfig,
    useBreakpointValue,
    SimpleGrid,
    Flex,
} from '@chakra-ui/react'

// Project Components
import CartSkeleton from '@salesforce/retail-react-app/app/pages/cart/partials/cart-skeleton'

import OrderSummary from '../../components/order-summary'
import ProductItem from '../../components/product-item'

//PARTIALS
import CartPolicies from './partials/cart-policies'
import CartSupport from './partials/cart-support'
import CartTitle from './partials/cart-title'
import CartSecondaryButtonGroup from './partials/cart-secondary-button-group'
import CartCta from './partials/cart-cta'
import CartShipping from './partials/cart-shipping'
import EmptyCart from './partials/empty-cart'
import CartRemoveWarning from './partials/cart-remove-warning'

// Hooks
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '../../hooks/use-wish-list'
import {useToast} from '../../hooks/use-toast'

// Constants
import {
    API_ERROR_MESSAGE,
    //EINSTEIN_RECOMMENDERS,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST,
} from '@salesforce/retail-react-app/app/constants'
import {TOAST_MESSAGE_ADDED_TO_WISHLIST} from '../../../app/constants'
import {useLocation} from 'react-router-dom'

// Utilities
import debounce from 'lodash/debounce'

import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment,
    useProducts,
    useShopperCustomersMutation,
    useShopperBasketsMutationHelper,
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import UnavailableProductConfirmationModal from './partials/modal/unavailable-product-confirmation-modal'
import CartBundle from '../../components/bundle/partials/cart-bundle'
import ConfirmationModal from '../../components/confirmation-modal'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {getPriceData} from '../../utils/product-utils'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min'
import SubscriptionItem from '../../components/subscription-item'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import ProductViewModal from '../../components/product-view-modal'
import BundleProductViewModal from '../../components/product-view-modal/bundle'
import FreeItemPromoBanner from './partials/free-item-promo-banner'
import FreeItemPromoDrawer from './partials/free-item-promo-drawer'
import {useMaxQuantityItems} from '../../hooks/use-max-quantity-items'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {getOrderAvailabilityStatus} from '../../utils/order-availability-utils'
import WarrantyConsistencyErrorModal from '../../components/order-summary/partials/warranty-consistency-error-modal'
import {useValidateWarrantyBasket} from '../../hooks/use-validate-warranty-basket'

const DEBOUNCE_WAIT = 750
const Cart = () => {
    const {data: basket, isLoading} = useCurrentBasket()
    const navigate = useNavigation()
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const {addLayer} = useGoogleTagManager()
    const {
        isOpen: isAddToCartModalOpen,
        onOpen: onAddToCartModalOpen,
        onClose: onAddToCartModalClose,
    } = useAddToCartModalContext()
    const hasGTMEventRun = useRef(false)
    const [activeToast, setActiveToast] = useState(false)
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''

    /***********EXTRACT BASKET LEVEL PROMOTIONS***********/
    const basketLevelPromotions = basket?.bonusDiscountLineItems || []
    const bonusProductPromotions = basketLevelPromotions.filter((promo) => {
        return promo?.promotionId.includes('Bonus_product')
    })

    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                perPricebook: true,
                expand: ['availability', 'images', 'prices', 'promotions'],
            },
            headers: {
                'x-debug': 'cart-index',
            },
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            },
        },
    )

    const {data: customer} = useCurrentCustomer()
    const {customerId, isRegistered} = customer
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const isBalanceExceeded = queryParams.get('isBalanceExceeded')
    const balanceDataError = queryParams.get('balanceDataError')
    const {
        isOpen: isEmployeeModalOpen,
        onOpen: onEmployeeModalOpen,
        onClose: onEmployeeModalClose,
    } = useDisclosure()
    const {
        isOpen: isFreeItemPromoOpen,
        onOpen: onFreeItemPromoOpen,
        onClose: onFreeItemPromoClose,
    } = useDisclosure()

    const {isQuantityAllowed, getMaxQuantityErrorMessage, getMaxQuantity} = useMaxQuantityItems()
    const {formatMessage} = useIntl()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const cartTitleMessage = defineMessage({
        defaultMessage: 'Basket',
        id: 'cart_title.title.cart_title',
    })

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: formatMessage(cartTitleMessage),
        description: formatMessage(cartTitleMessage),
        url: currentPath,
    }

    //******************BONUS ITEM PROMOTIONS******************/
    const [selectedFreeItems, setSelectedFreeItems] = useState({})
    const [selectedPromotion, setSelectedPromotion] = useState({})

    useEffect(() => {
        const savedPromoItems = localStorage.getItem('promoSelectedFreeItems')
        if (savedPromoItems) {
            setSelectedFreeItems(JSON.parse(savedPromoItems))
        }
    }, [])

    //if coutry is not shoppable, redirect to home page
    useEffect(() => {
        if (notShoppableCountry) {
            navigate('/')
        }
    }, [notShoppableCountry, navigate])

    /**
     * prevBonusGroupsRef holds a reference to the previous group of promotion groups
     */
    const prevBonusGroupsRef = useRef({})

    /**
     * A computed list of basket items
     * - Bonus items are sorted in groups using the bonusDiscountLineItemId
     * - Using the prevBonusGroupsRef we detect changes in the bonusGroups
     * - if a bonus group has been modified we display it first in the cart
     *
     * Groups are compared shallowly based on item `id` and order.
     * The value is memoized and updates when `basket.productItems` changes.
     */
    const basketItemsOrganized = useMemo(() => {
        if (!basket?.productItems) return []

        const bonusGroups = {}
        const regularItems = []

        for (const item of basket.productItems) {
            const bonusLineId = item?.bonusDiscountLineItemId

            if (bonusLineId) {
                if (!bonusGroups[bonusLineId]) {
                    bonusGroups[bonusLineId] = []
                }
                bonusGroups[bonusLineId].push(item)
            } else {
                regularItems.push(item)
            }
        }

        const prevBonusGroups = prevBonusGroupsRef.current
        const modifiedGroupIds = []

        // Detect changes
        for (const [groupId, items] of Object.entries(bonusGroups)) {
            const prevItems = prevBonusGroups[groupId] || []
            const changed =
                items.length !== prevItems.length ||
                items.some((item, index) => item.id !== prevItems[index]?.id)

            if (changed) {
                modifiedGroupIds.push(groupId)
            }
        }

        // Reorder groups
        const modifiedGroups = modifiedGroupIds.flatMap((id) => bonusGroups[id])
        const unmodifiedGroups = Object.entries(bonusGroups)
            .filter(([id]) => !modifiedGroupIds.includes(id))
            .flatMap(([, items]) => items)

        // Save current groups for next compare
        prevBonusGroupsRef.current = bonusGroups

        return [...modifiedGroups.reverse(), ...unmodifiedGroups.reverse(), ...regularItems]
    }, [basket?.productItems])

    const removeFreeItem = (promotionId, productId) => {
        setSelectedFreeItems((prev) => {
            const items = prev[promotionId]?.items || []
            const existing = items.find((item) => item.id === productId)

            let updatedItems
            if (existing && (existing.quantity || 1) > 1) {
                updatedItems = items.map((item) =>
                    item.id === productId ? {...item, quantity: (item.quantity || 1) - 1} : item,
                )
            } else {
                updatedItems = items.filter((item) => item.id !== productId)
            }

            return {
                ...prev,
                [promotionId]: {
                    ...prev[promotionId],
                    items: updatedItems,
                },
            }
        })
    }

    const history = useHistory()

    useEffect(() => {
        if (isAddToCartModalOpen) {
            onAddToCartModalClose()
        }
    }, [location.pathname])

    useEffect(() => {
        if (balanceDataError || isBalanceExceeded) {
            onEmployeeModalOpen()
            queryParams.delete('isBalanceExceeded')
            history.replace({search: queryParams.toString()})
        }
    }, [balanceDataError, isBalanceExceeded, queryParams, navigate])

    useEffect(() => {
        localStorage.setItem('promoSelectedFreeItems', JSON.stringify(selectedFreeItems))
        // console.log(selectedFreeItems)
    }, [selectedFreeItems])

    // Keep selectedFreeItems in sync with actual basket bonus items
    useEffect(() => {
        if (!basket?.productItems) return

        const basketBonusMap = {}
        for (const item of basket.productItems) {
            const promoId = item?.bonusDiscountLineItemId
            if (!promoId) continue
            if (!basketBonusMap[promoId]) basketBonusMap[promoId] = {}
            basketBonusMap[promoId][item.productId] =
                (basketBonusMap[promoId][item.productId] || 0) + 1
        }

        setSelectedFreeItems((prev) => {
            const next = {...prev}
            let changed = false

            for (const promoId of Object.keys(next)) {
                const basketProducts = basketBonusMap[promoId] || {}
                const prevItems = next[promoId]?.items || []

                const syncedItems = prevItems
                    .map((item) => {
                        const basketQty = basketProducts[item.id] || 0
                        if (basketQty === 0) return null
                        if ((item.quantity || 1) !== basketQty) {
                            return {...item, quantity: basketQty}
                        }
                        return item
                    })
                    .filter(Boolean)

                if (
                    syncedItems.length !== prevItems.length ||
                    syncedItems.some((s, i) => (s.quantity || 1) !== (prevItems[i]?.quantity || 1))
                ) {
                    next[promoId] = {...next[promoId], items: syncedItems}
                    changed = true
                }
            }

            return changed ? next : prev
        })
    }, [basket?.productItems])

    /***************** Product Bundles Old ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                expand: ['availability', 'images', 'prices', 'promotions'],
                perPricebook: true,
                select: '(data.(id,inventory,type))',
            },
            headers: {
                'x-debug': 'cart2-index',
            },
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            },
        },
    )

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory,
                        },
                    }
                }
            }
            updateProductsByItemId[productItem.itemId] = currentProduct
        })
        return updateProductsByItemId
    }, [basket, products, bundleChildProductData])

    /*****************GTM Event************************/
    useEffect(() => {
        if (!hasGTMEventRun.current && productsByItemId && !isLoading) {
            addLayer(polish_ecommerce_gtm_event.bind(this))
            addLayer(
                item_gtm_event.bind(this, {
                    event: 'view_cart',
                    currency: basket?.currency,
                    value: basket?.orderTotal || basket?.productTotal,
                    items: basket?.productItems?.length
                        ? basket?.productItems?.map((productItem) => {
                              const product = {
                                  ...productItem,
                                  ...(productsByItemId && productsByItemId[productItem.itemId]),
                                  price: productItem?.price,
                                  quantity: localQuantity[productItem.itemId]
                                      ? localQuantity[productItem.itemId]
                                      : productItem.quantity,
                              }
                              return {
                                  item_name: product?.c_defaultName,
                                  item_id: product?.id || product?.productId,
                                  item_category: product?.c_defaultCategoryName,
                                  item_variant: product?.c_subModelIdentifier,
                                  price: parseFloat(
                                      ((product?.price - product?.tax) / product?.quantity).toFixed(
                                          2,
                                      ),
                                  ),
                                  fullprice: getPriceData(product)?.isOnSale
                                      ? 'reduced'
                                      : 'fullprice',
                                  rating_points: '', //?Bazaarvoice rating
                                  erp_category: product?.c_MaterialGroup,
                                  prdha_category: product?.c_prdhaCategory,
                                  quantity: product?.quantity,
                              }
                          })
                        : [],
                }),
            )
            hasGTMEventRun.current = true
        }
    }, [isLoading, productsByItemId])

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment',
    )
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [localIsGiftItems, setLocalIsGiftItems] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const warrantyErrorModalRef = useRef(null)
    const {validateWarrantyBeforeAction, clearBasket} = useValidateWarrantyBasket({
        basket,
        onValidationFail: () => warrantyErrorModalRef.current?.onOpen(),
    })
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {isOpen: isShippingOpen, onClose: onShippingClose} = useDisclosure({defaultIsOpen: true})
    const toast = useToast()
    const modalProps = useDisclosure()
    const shippingMethodAdded = useRef(false)
    const setShippingMethodAdded = (value) => {
        shippingMethodAdded.current = value
    }

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    const {data: shippingMethods} = useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me',
            },
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled: !!basket?.basketId && shippingMethodAdded.current === false,
        },
    )

    useEffect(() => {
        if (!shippingMethods?.defaultShippingMethodId) {
            return
        }
        updateShippingMethodForShipmentsMutation.mutate({
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me',
            },
            body: {
                id: shippingMethods?.defaultShippingMethodId,
            },
        })
        setShippingMethodAdded(true)
    }, [shippingMethods])

    /************************* Error handling ***********************/
    const showError = () => {
        if (!activeToast) {
            setActiveToast(true)
            toast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error',
            })
        }
        setTimeout(() => {
            setActiveToast(false)
        }, 5000)
    }
    /************************* Error handling ***********************/

    /**************** Wishlist ****************/
    const {data: wishlist} = useWishList()

    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem',
    )
    const handleAddToWishlist = async (product) => {
        try {
            if (!customerId || !wishlist) {
                return
            }

            const isItemInWishlist = wishlist?.customerProductListItems?.find(
                (i) => i.productId === product?.id,
            )

            if (!isItemInWishlist) {
                await createCustomerProductListItem.mutateAsync({
                    parameters: {
                        listId: wishlist.id,
                        customerId,
                    },
                    body: {
                        // NOTE: APi does not respect quantity, it always adds 1
                        quantity: product.quantity,
                        productId: product.productId,
                        public: true,
                        priority: 1,
                        type: 'product',
                    },
                })
                toast({
                    title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                    status: 'success',
                })
            } else {
                toast({
                    title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                    status: 'info',
                    action: (
                        <Button
                            aria-label={formatMessage({
                                id: 'cart.button.view_wishlist.label',
                                defaultMessage: 'View Wishlist',
                            })}
                            variant="link"
                            onClick={() => navigate('/account/wishlist')}
                        >
                            {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                        </Button>
                    ),
                })
            }
        } catch {
            showError()
        }
    }
    /**************** Wishlist ****************/

    /***************************** Update Cart **************************/
    const handleUpdateCart = async (variant, quantity) => {
        // close the modal before handle the change
        onClose()
        // using try-catch is better than using onError callback since we have many mutation calls logic here
        try {
            setCartItemLoading(true)
            const productIds = basket?.productItems?.map(({productId}) => productId)

            // The user is selecting different variant, and it has not existed in basket
            if (selectedItem.id !== variant.productId && !productIds.includes(variant.productId)) {
                const item = {
                    productId: variant.productId,
                    quantity,
                    price: variant.price,
                }
                return await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId,
                    },
                    body: item,
                })
            }

            // The user is selecting different variant, and it has existed in basket
            // remove this item in the basket, change the quantity for the new selected variant in the basket
            if (selectedItem.id !== variant.productId && productIds.includes(variant.productId)) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId,
                    },
                })
                const basketItem = basket.productItems.find(
                    ({productId}) => productId === variant.productId,
                )
                const newQuantity = quantity + basketItem.quantity
                return await changeItemQuantity(newQuantity, basketItem)
            }

            // the user only changes quantity of the same variant
            if (selectedItem.quantity !== quantity) {
                return await changeItemQuantity(quantity, selectedItem)
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    quantity: bundleQuantity,
                })
            }

            if (itemsToBeUpdated.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId,
                    },
                    body: itemsToBeUpdated,
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleIsAGiftChange = async (product, checked) => {
        try {
            const previousVal = localIsGiftItems[product.itemId]
            setLocalIsGiftItems({
                ...localIsGiftItems,
                [product.itemId]: checked,
            })
            setCartItemLoading(true)
            setSelectedItem(product)
            await updateItemInBasketMutation.mutateAsync(
                {
                    parameters: {basketId: basket?.basketId, itemId: product.itemId},
                    body: {
                        productId: product.id,
                        quantity: parseInt(product.quantity),
                        gift: checked,
                    },
                },
                {
                    onSettled: () => {
                        // reset the state
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                    },
                    onSuccess: () => {
                        setLocalIsGiftItems({...localIsGiftItems, [product.itemId]: undefined})
                    },
                    onError: () => {
                        // reset the quantity to the previous value
                        setLocalIsGiftItems({...localIsGiftItems, [product.itemId]: previousVal})
                        showError()
                    },
                },
            )
        } catch (e) {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUnavailableProducts = async (unavailableProductIds) => {
        const productItems = basket?.productItems?.filter((item) =>
            unavailableProductIds?.includes(item.productId),
        )

        await Promise.all(
            productItems.map(async (item) => {
                await handleRemoveItem(item)
            }),
        )
    }

    /***************** Product Bundles ************************/
    const bundleMap = {}

    const enrichProduct = (item) => ({
        ...item,
        ...(productsByItemId && productsByItemId[item.itemId]),
        isProductUnavailable: !isProductsLoading ? !productsByItemId?.[item.itemId] : undefined,
        price: item.price,
        quantity: localQuantity[item.itemId] ? localQuantity[item.itemId] : item.quantity,
    })

    const allBundlesDetails = basket?.c_additionalData?.allBundlesDetails || {}

    basket?.productItems?.forEach((item) => {
        if (item.c_isBundle && item.c_UUIDBundle) {
            if (!bundleMap[item.c_UUIDBundle]) {
                bundleMap[item.c_UUIDBundle] = {
                    items: [],
                    details: allBundlesDetails[item.c_UUIDBundle] || {},
                }
            }
            bundleMap[item.c_UUIDBundle].items.push(enrichProduct(item))
        }
    })

    /***************** Subscriptions ************************/
    const allSubscriptionsDetails = basket?.c_allSubscriptionsDetails
        ? JSON.parse(basket.c_allSubscriptionsDetails)
        : {}

    /***************************** Update Cart **************************/

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId] || product.quantity
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)
        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity),
                },
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: async (updatedBasket) => {
                    setLocalQuantity({...localQuantity, [product.itemId]: undefined})

                    // Fire GTM event only after successful cart update
                    // Use the actual quantity from the updated basket response, not the user input
                    const updatedItem = updatedBasket?.productItems?.find(
                        (item) => item.itemId === product.itemId,
                    )
                    const actualQuantity = updatedItem?.quantity || quantity
                    const quantityDiff = actualQuantity - previousQuantity

                    // Only fire GTM event if quantity actually changed
                    if (quantityDiff !== 0) {
                        const isIncremented = quantityDiff > 0
                        const event = isIncremented ? 'add_to_cart' : 'remove_from_cart'
                        const productInfo = productsByItemId?.[product.itemId] || {}

                        await addLayer(polish_ecommerce_gtm_event)
                        await addLayer(
                            item_gtm_event.bind(this, {
                                event,
                                currency: basket?.currency,
                                bundle: 'false',
                                items: [
                                    {
                                        item_name:
                                            productInfo?.c_defaultName || product?.productName,
                                        item_id: product?.id || product?.productId,
                                        item_category:
                                            productInfo?.c_defaultCategoryName ||
                                            product?.c_categoryName,
                                        item_variant:
                                            productInfo?.c_subModelIdentifier ||
                                            product?.c_subModelIdentifier,
                                        price: parseFloat(
                                            (
                                                (product?.price - (product?.tax || 0)) /
                                                previousQuantity
                                            ).toFixed(2),
                                        ),
                                        fullprice: getPriceData(productInfo)?.isOnSale
                                            ? 'reduced'
                                            : 'fullprice',
                                        rating_points: '',
                                        erp_category:
                                            productInfo?.c_materialGroup ||
                                            product?.c_MaterialGroup,
                                        prdha_category:
                                            productInfo?.c_prdhaCategory ||
                                            product?.c_prdhaCategory,
                                        quantity: Math.abs(quantityDiff),
                                    },
                                ],
                            }),
                        )
                    }
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                },
            },
        )
    }, DEBOUNCE_WAIT)
    /************* Add Same Product Only, in case it has warranty associated *************/
    const addSameProductOnly = async (product) => {
        await addItemToNewOrExistingBasket([{productId: product.productId, quantity: 1}])
    }
    const handleChangeItemQuantity = async (product, value) => {
        // Get the actual product data to check if it's preorder/backorder
        const productData = productsByItemId?.[product.itemId]
        const orderStatus = getOrderAvailabilityStatus(productData)
        const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

        // For preorder/backorder, use custom preference limit; otherwise use stock level
        let maxAllowedQuantity
        if (isPreorderOrBackorder) {
            maxAllowedQuantity = getMaxQuantity(productData) || 2 // Default to 2 if no custom pref
        } else {
            maxAllowedQuantity = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1
        }

        const prevQty = product.quantity
        const isIncreasing = value > prevQty
        const hasWarranty = Boolean(product?.c_UUIDWarranty)

        if (hasWarranty && isIncreasing) {
            changeItemQuantity.cancel()
            setSelectedItem(product)
            setCartItemLoading(true)
            try {
                await addSameProductOnly(product)
                // do not update quantity
                return false
            } catch (e) {
                showError()
                return false
            } finally {
                setCartItemLoading(false)
                setSelectedItem(undefined)
            }
        }
        if (isIncreasing && !isQuantityAllowed(productData, 0, value)) {
            const maxQty = getMaxQuantity(productData)
            toast({
                title: getMaxQuantityErrorMessage(maxQty),
                status: 'error',
            })
            return false
        }
        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            // Flush last call to keep ui in sync with data.
            changeItemQuantity.flush()

            // Set the selected item to the current product to the modal acts on it.
            setSelectedItem(product)

            // Show the modal.
            modalProps.onOpen()

            // Return false as 0 isn't valid section.
            return false
        }

        // Cancel any pending handlers.
        changeItemQuantity.cancel()

        // Block if trying to exceed max allowed quantity
        if (value > maxAllowedQuantity) {
            return true
        }

        // If value hasn't changed, no action needed
        if (value === product.quantity) {
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }

    /***************************** Remove Item from basket **************************/
    const {onOpen: onRemoveOpen, onClose: onRemoveClose} = useDisclosure()
    const [removeMessages, setRemoveMessages] = useState([])

    const addRemoveMessage = (text, id) => {
        setRemoveMessages((prev) => {
            // Check if a message with the same id already exists
            const exists = prev.some((message) => message.id === id)
            if (exists) {
                // If it exists, return the previous array unchanged
                return prev
            }
            // Otherwise, add the new message
            return [...prev, {id, text}]
        })

        setTimeout(() => {
            setRemoveMessages((prev) => prev.filter((msg) => msg.id !== id))
        }, 5000)
    }

    const handleRemoveItem = async (product, name, timeToTrigger = 0, displayMessage = true) => {
        const createMessage = `${
            product?.c_variantIdentifier ||
            product?.name ||
            product?.productName ||
            name ||
            product?.c_defaultName
        } ${formatMessage({
            id: 'cart_default.remove_message.deleteMessage',
            defaultMessage: 'is being removed',
        })}`

        const id = product?.productId || product?.id

        if (displayMessage) {
            addRemoveMessage(createMessage, id)
            onRemoveOpen()
        }
        setSelectedItem(product)

        // Wait for the feedback message to be displayed
        setTimeout(async () => {
            try {
                await removeItemFromBasketMutation.mutateAsync(
                    {
                        parameters: {basketId: basket.basketId, itemId: product.itemId},
                    },
                    {
                        onSettled: () => {
                            setCartItemLoading(false)
                            setSelectedItem(undefined)
                        },
                        onError: () => {
                            showError()
                        },
                    },
                )
                if (product?.bonusDiscountLineItemId) {
                    removeFreeItem(product?.bonusDiscountLineItemId, product?.productId)
                }
            } catch (error) {
                setCartItemLoading(false)
                showError()
            }
        }, timeToTrigger)
    }

    const handleCancelRemove = () => {
        setSelectedItem(null)
        setRemoveMessages([])
        onRemoveClose()
    }

    //?Marketing cloud events
    useEffect(() => {
        window._etmc = window._etmc || []
        if (window._etmc) {
            basket &&
                basket?.productItems &&
                window._etmc.push([
                    'trackCart',
                    basket?.productItems?.map((item) => ({
                        item: item?.productId,
                        quantity: item?.quantity,
                        price: item?.price,
                        unique_id: item?.productId,
                        category: 'placeholder',
                    })),
                ])
        }
    }, [customer?.email, basket])

    /***********Render Recomended in the correct place************/
    const screenSize = useBreakpointValue({base: 'base', md: 'md', lg: 'lg'})

    /**********Styling ****************/
    const style = useMultiStyleConfig('Cart', {})
    /********* Rendering  UI **********/
    if (isLoading) {
        return <CartSkeleton />
    }

    if (!isLoading && !basket?.productItems?.length) {
        return <EmptyCart isRegistered={isRegistered} removeMessages={removeMessages} />
    }

    return (
        <Box
            {...style?.cart?.boxContainer}
            data-testid="sf-cart-container"
            layerStyle="page"
            data-gtm="cart-page"
        >
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            {isShippingOpen ? <CartShipping onClose={onShippingClose} /> : null}
            <Stack w="100%">
                <CartTitle />
                <Grid {...style?.cart?.containerGrid}>
                    <GridItem>
                        {removeMessages.length > 0 && (
                            <Box pb="medium.base">
                                {removeMessages.map((msg) => (
                                    <CartRemoveWarning
                                        key={msg.id}
                                        onClose={handleCancelRemove}
                                        message={msg.text}
                                    />
                                ))}
                            </Box>
                        )}
                        {bonusProductPromotions.length > 0 && (
                            <Flex
                                w="100%"
                                gap="small.base"
                                flexDir="column"
                                paddingBottom="medium.base"
                            >
                                {bonusProductPromotions.map((promo) => {
                                    return (
                                        <FreeItemPromoBanner
                                            onOpen={onFreeItemPromoOpen}
                                            key={promo?.id}
                                            promo={promo}
                                            setSelectedPromotion={setSelectedPromotion}
                                            selectedFreeItems={selectedFreeItems[promo?.id] || {}}
                                        />
                                    )
                                })}
                            </Flex>
                        )}
                        <SimpleGrid gap="medium.base">
                            {Object.entries(bundleMap).map(([bundleId, {items, details}], i) => (
                                <CartBundle
                                    key={`${bundleId}-${i}`}
                                    bundleItems={items}
                                    bundleDetails={details}
                                    handleRemoveItem={() => handleRemoveItem(items?.[0])}
                                />
                            ))}

                            {Object.entries(allSubscriptionsDetails).map(
                                ([bundleId, details], i) => {
                                    const matchingProduct = basket?.productItems.find(
                                        (item) => item?.c_UUIDSubscription === bundleId,
                                    )

                                    const enrichedDetails = {
                                        ...details,
                                        master: matchingProduct,
                                    }

                                    return (
                                        <SubscriptionItem
                                            key={`${bundleId}-${i}`}
                                            details={details}
                                            handleRemoveItem={() =>
                                                handleRemoveItem(
                                                    enrichedDetails?.master,
                                                    details?.subscriptionName,
                                                )
                                            }
                                        />
                                    )
                                },
                            )}

                            {basketItemsOrganized?.map((productItem, idx) => {
                                if (
                                    !productItem?.c_isBundle &&
                                    !productItem?.c_isSubscription &&
                                    !productItem?.c_isWarranty // TODO: for phase 2, change this control in order to hide only linked warranties (c_isWarranty && c_UUIDWarranty)
                                ) {
                                    return (
                                        <ProductItem
                                            key={
                                                productItem?.bonusProductLineItem &&
                                                productItem?.bonusDiscountLineItemId
                                                    ? `${productItem.productId}-${productItem?.bonusDiscountLineItemId}-${idx}`
                                                    : productItem.itemId
                                            }
                                            index={idx}
                                            secondaryActions={
                                                <CartSecondaryButtonGroup
                                                    // hasBonusProduct={
                                                    //     productItem?.bonusProductLineItem
                                                    // }
                                                    isAGift={
                                                        localIsGiftItems[productItem.itemId]
                                                            ? localIsGiftItems[productItem.itemId]
                                                            : productItem.gift
                                                    }
                                                    onIsAGiftChange={handleIsAGiftChange}
                                                    onAddToWishlistClick={handleAddToWishlist}
                                                    onEditClick={(product) => {
                                                        setSelectedItem(product)
                                                        onOpen()
                                                    }}
                                                    onRemoveItemClick={() => {
                                                        const item = productsByItemId
                                                            ? productsByItemId[productItem.itemId]
                                                            : {}

                                                        handleRemoveItem({
                                                            ...productItem,
                                                            name:
                                                                item?.c_variantIdentifier ||
                                                                item?.name ||
                                                                item?.productName,
                                                        })
                                                    }}
                                                    productData={productItem}
                                                />
                                            }
                                            product={{
                                                ...productItem,
                                                ...(productsByItemId &&
                                                    productsByItemId[productItem.itemId]),
                                                isProductUnavailable: !isProductsLoading
                                                    ? !productsByItemId?.[productItem.itemId]
                                                    : undefined,
                                                price: productItem.price,
                                                quantity: localQuantity[productItem.itemId]
                                                    ? localQuantity[productItem.itemId]
                                                    : productItem.quantity,
                                            }}
                                            onItemQuantityChange={handleChangeItemQuantity.bind(
                                                this,
                                                productItem,
                                            )}
                                            showLoading={
                                                isCartItemLoading &&
                                                selectedItem?.itemId === productItem.itemId
                                            }
                                            handleRemoveItem={handleRemoveItem}
                                            officinaEditParams={
                                                productItem?.c_officinaConfigurationId
                                            }
                                            officinaImage={
                                                productItem?.c_officinaConfigurationImage
                                            }
                                            isCart={true}
                                            onAddToCartModalOpen={onAddToCartModalOpen}
                                        />
                                    )
                                }
                            })}
                            {screenSize === 'lg' ? (
                                <Box id="cart_product_recommendations" overflowX="hidden"></Box>
                            ) : (
                                <></>
                            )}
                        </SimpleGrid>
                        <Box>
                            {isOpen && !selectedItem.bundledProductItems && (
                                <ProductViewModal
                                    isOpen={isOpen}
                                    onOpen={onOpen}
                                    onClose={onClose}
                                    product={selectedItem}
                                    updateCart={(variant, quantity) =>
                                        handleUpdateCart(variant, quantity)
                                    }
                                />
                            )}
                            {isOpen && selectedItem.bundledProductItems && (
                                <BundleProductViewModal
                                    isOpen={isOpen}
                                    onOpen={onOpen}
                                    onClose={onClose}
                                    product={selectedItem}
                                    updateCart={(product, quantity, childProducts) =>
                                        handleUpdateBundle(product, quantity, childProducts)
                                    }
                                />
                            )}
                        </Box>
                    </GridItem>
                    <GridItem>
                        <Stack {...style?.cart?.orderSummaryContainer}>
                            <OrderSummary
                                showPromoCodeForm={!basket?.couponItems?.length}
                                isEstimate={true}
                                basket={basket}
                                handleUnavailableProducts={handleUnavailableProducts}
                                showSubscriptionsCheck={basket?.c_containsSubscription}
                                validateWarrantyBeforeAction={validateWarrantyBeforeAction}
                            />
                            {/** gifts and returns section */}
                            <Box {...style?.cart?.checkoutItem}>
                                <CartPolicies />
                            </Box>
                            {/**Support Section */}
                            <Box {...style?.cart?.checkoutItemWhite}>
                                <CartSupport />
                            </Box>
                            <Box {...style?.cart?.checkoutItemWhite}>
                                <CartCta />
                            </Box>
                        </Stack>
                    </GridItem>
                    {screenSize === 'md' || screenSize === 'base' || screenSize === 'sm' ? (
                        <GridItem overflowX="hidden" id="cart_product_recommendations"></GridItem>
                    ) : (
                        <></>
                    )}
                </Grid>
            </Stack>
            <UnavailableProductConfirmationModal
                productItems={basket?.productItems}
                handleUnavailableProducts={handleUnavailableProducts}
            />
            <WarrantyConsistencyErrorModal
                ref={warrantyErrorModalRef}
                handleClearBasket={clearBasket}
            />
            <ConfirmationModal
                data-testid="excedd-employee-balance-modal"
                onAlternateAction={async () => {
                    onEmployeeModalClose()
                }}
                title={formatMessage({
                    defaultMessage: 'Error',
                    id: 'confirmation_employee_modal.title',
                })}
                description={formatMessage({
                    defaultMessage: 'Some products exceed the employee balance',
                    id: 'confirmation_employee_modal.description',
                })}
                isOpen={isEmployeeModalOpen}
            />
            <FreeItemPromoDrawer
                isOpen={isFreeItemPromoOpen}
                onClose={onFreeItemPromoClose}
                setSelectedFreeItems={setSelectedFreeItems}
                selectedPromotion={selectedPromotion}
                basketItems={basket?.productItems}
                basketId={basket.basketId}
                handleRemoveItem={handleRemoveItem}
                productsByItemId={productsByItemId}
            />
        </Box>
    )
}

Cart.getTemplateName = () => 'cart'

export default Cart
